import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { supabase } from "../lib/supabase";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [signingIn, setSigningIn] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const handleGoogleSignIn = async () => {
    if (!supabase) {
      console.warn('Supabase not configured. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in Vercel environment variables.');
      return;
    }
    setSigningIn(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
          redirectTo: window.location.origin + '/auth/callback',
          scopes: 'openid email profile',
        },
      });
      if (error) throw error;
    } catch (err) {
      console.error('Sign-in error:', err);
      setSigningIn(false);
    }
  };

  return (
    <motion.nav
      data-testid="navbar"
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.4 }}
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-md border-b border-zinc-200 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center gap-2" data-testid="navbar-logo">
          <span className="fn-key text-sm px-2 py-1">FN</span>
          <span
            style={{ fontFamily: "Manrope, sans-serif" }}
            className="text-xl font-bold tracking-tight text-zinc-900"
          >
            ClawdVoice
          </span>
        </div>

        {/* Nav links */}
        <div className="hidden md:flex items-center gap-8">
          {[
            ["Features", "features"],
            ["How it works", "how-it-works"],
            ["FAQ", "faq"],
          ].map(([label, id]) => (
            <button
              key={id}
              onClick={() => scrollTo(id)}
              data-testid={`nav-${id}`}
              className="text-sm font-medium text-zinc-500 hover:text-zinc-900 transition-colors"
            >
              {label}
            </button>
          ))}
        </div>

        {/* CTAs */}
        <div className="flex items-center gap-3">
          <button
            data-testid="navbar-signin-btn"
            onClick={handleGoogleSignIn}
            disabled={signingIn}
            className="inline-flex items-center gap-2 bg-white border border-zinc-200 text-zinc-700 text-sm font-medium px-4 py-2.5 rounded-md hover:bg-zinc-50 hover:border-zinc-300 transition-all active:scale-95 disabled:opacity-60"
          >
            {/* Google icon */}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            {signingIn ? 'Redirecting...' : 'Sign in with Google'}
          </button>

          <a
            data-testid="navbar-calendly-btn"
            href="https://calendly.com/angaddubey10/new-meeting"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-zinc-900 text-white text-sm font-semibold px-5 py-2.5 rounded-md hover:bg-zinc-700 transition-all active:scale-95"
          >
            Meet with Founders
          </a>
        </div>
      </div>
    </motion.nav>
  );
}
